import { Fragment } from "react";
import { IconHexagonsOff, IconChevronDown, IconLayoutSidebarLeftCollapse, IconMenu2 } from "@tabler/icons-react";
import { NavLink } from "react-router-dom";
import { CopyrightFooter } from "src/privacy-policy/CopyrightFooter";
import { classNames } from "src/core/utilities/classNames";
import { AccountMenu } from "./AccountMenu";
import { FrameHeading, FrameLogo, RailLink } from "./FrameLinks";
import { SPATIAL_EASE } from "./frameMotion";
import { useFrameMenu } from "./useFrameMenu";

export const RAIL_WIDTH = { collapsed: "6rem", expanded: "18.5rem" };

interface Props {
    open: boolean;
    onToggle: () => void;
}

const Collapsed = ({ onToggle }: Pick<Props, "onToggle">) => {
    const { main } = useFrameMenu();

    return (
        <div className="flex flex-col items-center h-full py-3 px-2">
            <button
                type="button"
                onClick={onToggle}
                aria-label="Open the menu"
                aria-expanded={false}
                className="grid place-items-center w-12 h-12 rounded-full transition-all hover:bg-base-content/10 active:rounded-2xl"
            >
                <IconMenu2 className="w-6 h-6" />
            </button>

            <div className="grid gap-1 w-full mt-3">
                {main.map((item) => (
                    <NavLink key={item.to} to={item.to} end={item.bottomEnd} className="group flex flex-col items-center gap-1 pt-1.5 pb-2">
                        {({ isActive }) => (
                            <>
                                <span className="relative grid place-items-center w-14 h-8">
                                    {/* The pill grows out from the centre, as Material's rail does */}
                                    <span
                                        className={classNames(
                                            "absolute inset-0 rounded-full bg-indicator transition-[transform,opacity] duration-300",
                                            isActive ? "scale-x-100 opacity-100" : "scale-x-50 opacity-0 group-hover:opacity-40 group-hover:scale-x-100"
                                        )}
                                        style={{ transitionTimingFunction: SPATIAL_EASE }}
                                    />

                                    <item.icon className={classNames("w-6 h-6 relative", { "text-on-indicator": isActive })} />
                                </span>

                                <span
                                    className={classNames(
                                        "text-xs leading-tight text-center max-w-20",
                                        isActive ? "font-extrabold" : "font-semibold text-base-content/75"
                                    )}
                                >
                                    {item.shortLabel ?? item.label}
                                </span>
                            </>
                        )}
                    </NavLink>
                ))}
            </div>

            <div className="mt-auto">
                <AccountMenu compact />
            </div>
        </div>
    );
};

const Expanded = ({ onToggle }: Pick<Props, "onToggle">) => {
    const { main, groups, account } = useFrameMenu();
    const ministries = groups.find((group) => group.key === "ministries");
    const others = groups.filter((group) => group !== ministries);

    return (
        <div className="flex flex-col h-full overflow-y-auto overflow-x-hidden p-3">
            <FrameLogo
                before={
                    <button
                        type="button"
                        onClick={onToggle}
                        aria-label="Close the menu"
                        aria-expanded
                        className="grid place-items-center w-12 h-12 -ml-2 rounded-full transition-all hover:bg-base-content/10 active:rounded-2xl"
                    >
                        <IconLayoutSidebarLeftCollapse className="w-6 h-6" />
                    </button>
                }
            />

            <AccountMenu />

            <nav className="grid gap-px mt-1">
                {main.map((item) => (
                    <Fragment key={item.to}>
                        <RailLink link={{ to: item.to, label: item.label, icon: item.icon, end: item.end }} />

                        {/* A person's ministries sit under Ministries itself, rather than under a second heading of the same name */}
                        {item.to === "/ministries" && ministries && (
                            <>
                                {ministries.links.map((link) => (
                                    <RailLink key={link.to} link={link} nested />
                                ))}

                                {ministries.inactive && ministries.inactive.length > 0 && (
                                    <details className="group/inactive">
                                        <summary className="flex items-center gap-3 min-h-10 pl-12 pr-4 rounded-full cursor-pointer list-none text-[0.85rem] font-medium text-base-content/60 hover:bg-base-content/10 [&::-webkit-details-marker]:hidden">
                                            <IconHexagonsOff className="w-[1.1rem] h-[1.1rem]" />
                                            Past ministries
                                            <IconChevronDown className="ml-auto transition-transform group-open/inactive:rotate-180" />
                                        </summary>

                                        <div className="grid gap-px">
                                            {ministries.inactive.map((link) => (
                                                <RailLink key={link.to} link={link} nested />
                                            ))}
                                        </div>
                                    </details>
                                )}
                            </>
                        )}
                    </Fragment>
                ))}
            </nav>

            {others.map((group) => (
                <section key={group.key} className="mt-2 pt-2 border-t border-base-content/10">
                    <FrameHeading>{group.label}</FrameHeading>

                    <div className="grid gap-px">
                        {group.links.map((link) => (
                            <RailLink key={link.to} link={link} />
                        ))}
                    </div>
                </section>
            ))}

            <div className="grid gap-px mt-2 pt-2 border-t border-base-content/10">
                {account.map((link) => (
                    <RailLink key={link.to} link={link} />
                ))}
            </div>

            <footer className="mt-auto pt-6 pb-1 text-xs">
                <CopyrightFooter />
            </footer>
        </div>
    );
};

/**
 * The laptop's rail. Collapsed it holds the main places; the menu button opens it to
 * the whole list, pushing the page over rather than covering it.
 */
export const NavigationRail = ({ open, onToggle }: Props) => (
    <aside
        aria-label="Sidebar"
        className={classNames(
            "hidden lg:block fixed inset-y-0 left-0 z-50 bg-frame transition-[width] duration-500 motion-reduce:transition-none",
            // clipped while it grows; left open when collapsed, so the account menu can open beside it
            open ? "overflow-hidden" : "overflow-visible"
        )}
        style={{ width: open ? RAIL_WIDTH.expanded : RAIL_WIDTH.collapsed, transitionTimingFunction: SPATIAL_EASE }}
    >
        {/* Fixed at the expanded width, so the list is clipped while the rail grows rather than reflowing */}
        <div className="h-full" style={{ width: open ? RAIL_WIDTH.expanded : RAIL_WIDTH.collapsed }}>
            {open ? <Expanded onToggle={onToggle} /> : <Collapsed onToggle={onToggle} />}
        </div>
    </aside>
);
